import React, { useRef, useEffect, useState } from 'react';

/**
 * Seamless idle loop video player.
 * Uses a single video element with a brief opacity crossfade near the loop
 * point to mask the hard restart. The video fades out over the last 0.4s,
 * then snaps back to the start and fades in over 0.4s, creating the
 * illusion of continuous motion.
 * If the video fails to load/decode, degrades gracefully to the static
 * poster image instead of a frozen/blank tile.
 */
const IdleLoopVideo = ({ src, poster, className, testId }) => {
  const videoRef = useRef(null);
  const [opacity, setOpacity] = useState(1);
  const [failed, setFailed] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src || failed) return;

    const FADE_DURATION = 0.4;

    const tick = () => {
      if (!video.duration || video.paused) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const remaining = video.duration - video.currentTime;
      if (remaining <= FADE_DURATION) {
        setOpacity(remaining / FADE_DURATION);
      } else if (video.currentTime <= FADE_DURATION) {
        setOpacity(Math.min(1, video.currentTime / FADE_DURATION));
      } else {
        setOpacity(1);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [src, failed]);

  if (!src) return null;

  if (failed) {
    if (!poster) return null;
    return (
      <img
        src={poster}
        alt=""
        className={className}
        data-testid={testId ? `${testId}-fallback` : undefined}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      src={src}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      poster={poster}
      className={className}
      style={{ opacity, transition: 'opacity 0.1s linear' }}
      data-testid={testId}
      onCanPlay={(e) => e.target.play().catch(() => {})}
      onError={() => setFailed(true)}
    />
  );
};

export { IdleLoopVideo };
